import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import axios from 'axios';
import { 
  Kanban, Plus, Search, DollarSign, User, Building, Phone, Mail, 
  Calendar, GripVertical, X, Check, AlertTriangle, Trash2, Edit2,
  TrendingUp, Users, Target, XCircle, ChevronDown, ChevronUp, Eye
} from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { id: 'lead', name: 'Lead', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-50' },
  { id: 'qualification', name: 'Qualificação', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50' },
  { id: 'proposal', name: 'Proposta', color: 'bg-purple-500', textColor: 'text-purple-700', bgLight: 'bg-purple-50' },
  { id: 'negotiation', name: 'Negociação', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
  { id: 'won', name: 'Fechado Ganho', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50' },
  { id: 'lost', name: 'Fechado Perdido', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
];

const SalesKanban = () => {
  const { token } = useAdminAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [draggedLead, setDraggedLead] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showWonModal, setShowWonModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [showStats, setShowStats] = useState(true);
  const [existingClients, setExistingClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    company: '',
    phone: '',
    estimated_value: '',
    description: ''
  });
  const [wonFormData, setWonFormData] = useState({
    contract_total_value: '',
    contract_installments: 1,
    contract_due_dates: [],
    first_due_date: ''
  });
  const [lostReason, setLostReason] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchExistingClients();
  }, []);

  const fetchExistingClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/admin/sales/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/sales/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.stage === targetStage) {
      setDraggedLead(null);
      return;
    }

    // Handle special stages
    if (targetStage === 'won') {
      setPendingStageChange({ lead: draggedLead, stage: targetStage });
      // Define primeira data de vencimento como próximo mês, dia 10
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      nextMonth.setDate(10);
      const firstDueDate = nextMonth.toISOString().split('T')[0];
      
      setWonFormData({
        contract_total_value: draggedLead.estimated_value || '',
        contract_installments: 1,
        contract_due_dates: [],
        first_due_date: firstDueDate
      });
      setShowWonModal(true);
      setDraggedLead(null);
      return;
    }

    if (targetStage === 'lost') {
      setPendingStageChange({ lead: draggedLead, stage: targetStage });
      setLostReason('');
      setShowLostModal(true);
      setDraggedLead(null);
      return;
    }

    // Normal stage change
    await updateLeadStage(draggedLead.id, targetStage);
    setDraggedLead(null);
  };

  const updateLeadStage = async (leadId, stage, extraData = {}) => {
    try {
      const response = await axios.patch(
        `${API}/admin/sales/leads/${leadId}/stage`,
        { stage, ...extraData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show success message for won
      if (stage === 'won' && response.data.client_created) {
        alert(`✅ Cliente e contrato criados com sucesso!\n\nSenha temporária do cliente: ${response.data.client_temp_password}\n\nEnvie esta senha para o cliente.`);
      }

      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error updating lead stage:', error);
      alert('Erro ao atualizar estágio');
    }
  };

  const handleConfirmWon = async () => {
    if (!pendingStageChange) return;
    
    // Gerar datas de vencimento automaticamente com base na primeira data
    const installments = parseInt(wonFormData.contract_installments) || 1;
    const dueDates = [];
    
    if (wonFormData.first_due_date) {
      const firstDate = new Date(wonFormData.first_due_date + 'T12:00:00');
      for (let i = 0; i < installments; i++) {
        const dueDate = new Date(firstDate);
        dueDate.setMonth(dueDate.getMonth() + i);
        dueDates.push(dueDate.toISOString().split('T')[0]);
      }
    }
    
    await updateLeadStage(pendingStageChange.lead.id, 'won', {
      contract_total_value: parseFloat(wonFormData.contract_total_value) || pendingStageChange.lead.estimated_value,
      contract_installments: installments,
      contract_due_dates: dueDates
    });
    
    setShowWonModal(false);
    setPendingStageChange(null);
  };

  const handleConfirmLost = async () => {
    if (!pendingStageChange) return;
    
    await updateLeadStage(pendingStageChange.lead.id, 'lost', {
      lost_reason: lostReason
    });
    
    setShowLostModal(false);
    setPendingStageChange(null);
    setLostReason('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLead) {
        await axios.put(
          `${API}/admin/sales/leads/${editingLead.id}`,
          { ...formData, estimated_value: parseFloat(formData.estimated_value) || 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/admin/sales/leads`,
          { ...formData, estimated_value: parseFloat(formData.estimated_value) || 0 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      setShowModal(false);
      setEditingLead(null);
      setFormData({ contact_name: '', email: '', company: '', phone: '', estimated_value: '', description: '' });
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Erro ao salvar lead');
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      contact_name: lead.contact_name || '',
      email: lead.email || '',
      company: lead.company || '',
      phone: lead.phone || '',
      estimated_value: lead.estimated_value || '',
      description: lead.description || ''
    });
    setClientSearch(lead.contact_name || '');
    setSelectedClient(null);
    setShowModal(true);
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setClientSearch(client.full_name);
    setFormData({
      ...formData,
      contact_name: client.full_name,
      email: client.email,
      company: client.company || '',
      phone: client.phone || ''
    });
    setShowClientDropdown(false);
  };

  const handleClientSearchChange = (value) => {
    setClientSearch(value);
    setFormData({ ...formData, contact_name: value });
    setSelectedClient(null);
    setShowClientDropdown(value.length > 0);
  };

  const filteredClients = existingClients.filter(client => {
    const searchLower = clientSearch.toLowerCase();
    return (
      client.full_name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.company?.toLowerCase().includes(searchLower)
    );
  }).slice(0, 5);

  const handleDelete = async (leadId) => {
    if (!window.confirm('Tem certeza que deseja excluir este lead?')) return;
    
    try {
      await axios.delete(`${API}/admin/sales/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      console.error('Error deleting lead:', error);
      alert('Erro ao excluir lead');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const filteredLeads = leads.filter(lead => {
    const searchTerm = search.toLowerCase();
    return (
      lead.contact_name?.toLowerCase().includes(searchTerm) ||
      lead.email?.toLowerCase().includes(searchTerm) ||
      lead.company?.toLowerCase().includes(searchTerm)
    );
  });

  const getLeadsByStage = (stageId) => {
    return filteredLeads.filter(lead => lead.stage === stageId);
  };

  const getStageTotal = (stageId) => {
    return getLeadsByStage(stageId).reduce((sum, lead) => sum + (lead.estimated_value || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-arcano-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Kanban className="text-arcano-blue" />
            Pipeline de Vendas
          </h1>
          <p className="text-gray-600">Gerencie suas oportunidades de negócio</p>
        </div>
        <button
          onClick={() => {
            setEditingLead(null);
            setFormData({ contact_name: '', email: '', company: '', phone: '', estimated_value: '', description: '' });
            setClientSearch('');
            setSelectedClient(null);
            setShowClientDropdown(false);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors"
        >
          <Plus size={20} />
          Nova Oportunidade
        </button>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <button
            onClick={() => setShowStats(!showStats)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <span className="font-semibold text-gray-900">Resumo do Pipeline</span>
            {showStats ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
          
          {showStats && (
            <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-1">
                  <Target size={18} className="text-blue-600" />
                  <span className="text-sm text-blue-800">Pipeline Ativo</span>
                </div>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(stats.summary?.total_active_pipeline)}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp size={18} className="text-green-600" />
                  <span className="text-sm text-green-800">Total Ganho</span>
                </div>
                <p className="text-xl font-bold text-green-700">{formatCurrency(stats.summary?.total_won)}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle size={18} className="text-red-600" />
                  <span className="text-sm text-red-800">Total Perdido</span>
                </div>
                <p className="text-xl font-bold text-red-700">{formatCurrency(stats.summary?.total_lost)}</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-1">
                  <Users size={18} className="text-purple-600" />
                  <span className="text-sm text-purple-800">Taxa de Conversão</span>
                </div>
                <p className="text-xl font-bold text-purple-700">{(stats.summary?.conversion_rate || 0).toFixed(1)}%</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nome, email ou empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 pb-6">
        {STAGES.map((stage) => (
          <div
            key={stage.id}
            className="flex flex-col bg-gray-50 rounded-xl shadow-md border-2 border-gray-200 min-w-0"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
          >
            {/* Stage Header */}
            <div className={`${stage.color} text-white p-4 rounded-t-xl`}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-base xl:text-lg truncate">{stage.name}</h3>
                <span className="bg-white/30 px-2 xl:px-3 py-1 rounded-full text-sm font-semibold flex-shrink-0 ml-2">
                  {getLeadsByStage(stage.id).length}
                </span>
              </div>
              <p className="text-sm xl:text-base text-white/90 mt-2 font-medium">
                {formatCurrency(getStageTotal(stage.id))}
              </p>
            </div>

            {/* Cards */}
            <div className="flex-1 p-3 space-y-3 min-h-[400px] max-h-[calc(100vh-350px)] overflow-y-auto">
              {getLeadsByStage(stage.id).map((lead) => (
                <div
                  key={lead.id}
                  draggable={stage.id !== 'won' && stage.id !== 'lost'}
                  onDragStart={(e) => handleDragStart(e, lead)}
                  className={`bg-white rounded-lg shadow border-2 border-gray-100 p-4 cursor-move hover:shadow-lg hover:border-gray-300 transition-all ${
                    draggedLead?.id === lead.id ? 'opacity-50 scale-95' : ''
                  } ${stage.id === 'won' || stage.id === 'lost' ? 'cursor-default' : ''}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {stage.id !== 'won' && stage.id !== 'lost' && (
                        <GripVertical size={18} className="text-gray-400" />
                      )}
                      <span className="font-bold text-gray-900">{lead.contact_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {/* View button - always visible */}
                      <button
                        onClick={() => {
                          setViewingLead(lead);
                          setShowViewModal(true);
                        }}
                        className="p-1.5 hover:bg-blue-50 rounded-md transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye size={16} className="text-arcano-blue" />
                      </button>
                      {stage.id !== 'won' && stage.id !== 'lost' && (
                        <button
                          onClick={() => handleEdit(lead)}
                          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <Edit2 size={16} className="text-gray-500" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Mail size={14} />
                      <span className="truncate">{lead.email}</span>
                    </div>
                    {lead.company && (
                      <div className="flex items-center gap-2">
                        <Building size={14} />
                        <span className="truncate">{lead.company}</span>
                      </div>
                    )}
                    {lead.phone && (
                      <div className="flex items-center gap-2">
                        <Phone size={14} />
                        <span>{lead.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <span className={`text-base font-bold ${stage.textColor}`}>
                      {formatCurrency(lead.estimated_value)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>

                  {stage.id === 'won' && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-2 rounded-md font-medium">
                      <Check size={16} />
                      <span>Cliente e contrato criados</span>
                    </div>
                  )}

                  {stage.id === 'lost' && lead.lost_reason && (
                    <div className="mt-3 text-sm text-red-700 bg-red-100 px-3 py-2 rounded-md">
                      <span className="font-medium">Motivo:</span> {lead.lost_reason}
                    </div>
                  )}
                </div>
              ))}

              {getLeadsByStage(stage.id).length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 xl:py-16 text-gray-400 border-2 border-dashed border-gray-300 rounded-lg m-2 flex-1">
                  <div className={`w-10 h-10 xl:w-12 xl:h-12 ${stage.bgLight} rounded-full flex items-center justify-center mb-3`}>
                    <Plus size={20} className={stage.textColor} />
                  </div>
                  <p className="text-xs xl:text-sm font-medium text-center px-2">Arraste cards aqui</p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowClientDropdown(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingLead ? 'Editar Oportunidade' : 'Nova Oportunidade'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Contato *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => handleClientSearchChange(e.target.value)}
                    onFocus={() => clientSearch.length > 0 && setShowClientDropdown(true)}
                    required
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    placeholder="Digite para buscar clientes existentes..."
                    data-testid="contact-name-input"
                  />
                  
                  {/* Dropdown de clientes existentes */}
                  {showClientDropdown && filteredClients.length > 0 && !editingLead && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                      <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500 font-medium">
                        Clientes cadastrados
                      </div>
                      {filteredClients.map((client) => (
                        <button
                          key={client.email}
                          type="button"
                          onClick={() => handleSelectClient(client)}
                          className="w-full px-3 py-2 text-left hover:bg-arcano-blue/10 border-b border-gray-100 last:border-0 transition-colors"
                          data-testid={`client-option-${client.email}`}
                        >
                          <div className="font-medium text-gray-900">{client.full_name}</div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <Mail size={12} />
                            {client.email}
                            {client.company && (
                              <>
                                <span className="mx-1">•</span>
                                <Building size={12} />
                                {client.company}
                              </>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedClient && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
                    <Check size={16} />
                    <span>Cliente existente selecionado: <strong>{selectedClient.full_name}</strong></span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedClient(null);
                        setClientSearch('');
                        setFormData({ ...formData, contact_name: '', email: '', company: '', phone: '' });
                      }}
                      className="ml-auto text-green-800 hover:text-green-900"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                {!editingLead && !selectedClient && clientSearch.length > 0 && filteredClients.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Nenhum cliente encontrado. Um novo lead será criado.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={selectedClient !== null}
                    className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent ${selectedClient ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="email@exemplo.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                      placeholder="Empresa"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                      placeholder="(00) 0 0000-0000"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor Estimado</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.estimated_value}
                    onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-arcano-blue focus:border-transparent"
                  placeholder="Observações sobre a oportunidade..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-arcano-blue text-white px-4 py-2 rounded-md hover:bg-arcano-blue-dark transition-colors"
                >
                  {editingLead ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Won Modal */}
      {showWonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-4 border-b bg-green-50">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                <Check className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">Fechar como Ganho</h3>
                <p className="text-sm text-green-600">Será criado um cliente e contrato</p>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Atenção:</strong> Ao confirmar, um novo cliente será criado (se não existir) e um contrato será gerado automaticamente.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Contrato</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={wonFormData.contract_total_value}
                    onChange={(e) => setWonFormData({ ...wonFormData, contract_total_value: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Valor total do contrato"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de Parcelas</label>
                <input
                  type="number"
                  min="1"
                  max="48"
                  value={wonFormData.contract_installments}
                  onChange={(e) => setWonFormData({ ...wonFormData, contract_installments: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <Calendar size={16} />
                    Primeiro Vencimento
                  </div>
                </label>
                <input
                  type="date"
                  value={wonFormData.first_due_date}
                  onChange={(e) => setWonFormData({ ...wonFormData, first_due_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  As demais parcelas serão mensais a partir desta data
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowWonModal(false);
                    setPendingStageChange(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmWon}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Confirmar e Criar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lost Modal */}
      {showLostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center gap-3 p-4 border-b bg-red-50">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <XCircle className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-800">Fechar como Perdido</h3>
                <p className="text-sm text-red-600">O lead ficará apenas no histórico</p>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Perda</label>
                <textarea
                  value={lostReason}
                  onChange={(e) => setLostReason(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Por que a oportunidade foi perdida?"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowLostModal(false);
                    setPendingStageChange(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmLost}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Confirmar Perda
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Lead Details Modal */}
      {showViewModal && viewingLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b bg-arcano-blue text-white rounded-t-lg">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Eye size={20} />
                Detalhes da Oportunidade
              </h3>
              <button 
                onClick={() => {
                  setShowViewModal(false);
                  setViewingLead(null);
                }} 
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  STAGES.find(s => s.id === viewingLead.stage)?.bgLight || 'bg-gray-100'
                } ${STAGES.find(s => s.id === viewingLead.stage)?.textColor || 'text-gray-700'}`}>
                  {STAGES.find(s => s.id === viewingLead.stage)?.name || viewingLead.stage}
                </span>
                <span className="text-sm text-gray-500">
                  Criado em {new Date(viewingLead.created_at).toLocaleDateString('pt-BR')}
                </span>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide">Informações de Contato</h4>
                
                <div className="flex items-center gap-3">
                  <User size={18} className="text-arcano-blue" />
                  <div>
                    <p className="text-xs text-gray-500">Nome</p>
                    <p className="font-medium text-gray-900">{viewingLead.contact_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-arcano-blue" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{viewingLead.email}</p>
                  </div>
                </div>

                {viewingLead.company && (
                  <div className="flex items-center gap-3">
                    <Building size={18} className="text-arcano-blue" />
                    <div>
                      <p className="text-xs text-gray-500">Empresa</p>
                      <p className="font-medium text-gray-900">{viewingLead.company}</p>
                    </div>
                  </div>
                )}

                {viewingLead.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-arcano-blue" />
                    <div>
                      <p className="text-xs text-gray-500">Telefone</p>
                      <p className="font-medium text-gray-900">{viewingLead.phone}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Financial Info */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <DollarSign size={18} className="text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Valor Estimado</p>
                    <p className="font-bold text-green-700 text-lg">{formatCurrency(viewingLead.estimated_value)}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {viewingLead.description && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-2">Descrição</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{viewingLead.description}</p>
                </div>
              )}

              {/* Won Info */}
              {viewingLead.stage === 'won' && viewingLead.won_at && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                    <Check size={16} />
                    Fechado Ganho
                  </h4>
                  <p className="text-sm text-green-700">
                    Data: {new Date(viewingLead.won_at).toLocaleDateString('pt-BR')}
                  </p>
                  {viewingLead.contract_id && (
                    <p className="text-sm text-green-700 mt-1">
                      Contrato criado: #{viewingLead.contract_id.substring(0, 8)}
                    </p>
                  )}
                </div>
              )}

              {/* Lost Info */}
              {viewingLead.stage === 'lost' && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 text-sm uppercase tracking-wide mb-2 flex items-center gap-2">
                    <XCircle size={16} />
                    Fechado Perdido
                  </h4>
                  {viewingLead.lost_at && (
                    <p className="text-sm text-red-700">
                      Data: {new Date(viewingLead.lost_at).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                  {viewingLead.lost_reason && (
                    <p className="text-sm text-red-700 mt-1">
                      Motivo: {viewingLead.lost_reason}
                    </p>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="text-xs text-gray-400 pt-2 border-t flex justify-between">
                <span>ID: {viewingLead.id?.substring(0, 8)}</span>
                {viewingLead.updated_at && (
                  <span>Atualizado: {new Date(viewingLead.updated_at).toLocaleDateString('pt-BR')}</span>
                )}
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-lg">
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingLead(null);
                }}
                className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesKanban;
